import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../authContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  redirectTo?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requireAuth = true, 
  redirectTo = '/signin' 
}) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [isCheckingAuth, setIsCheckingAuth] = React.useState(true);

  // Wait for auth check to complete before redirecting
  React.useEffect(() => {
    // Check if we have auth data (token or session phone)
    const hasToken = document.cookie.includes('token=');
    const hasSessionPhone = sessionStorage.getItem('phone');
    
    // If we have auth data but isAuthenticated is false, wait a bit for auth context to initialize
    if ((hasToken || hasSessionPhone) && !isAuthenticated) {
      const timer = setTimeout(() => {
        setIsCheckingAuth(false);
      }, 200); // Small delay to allow auth context to initialize
      return () => clearTimeout(timer);
    } else {
      setIsCheckingAuth(false);
    }
  }, [isAuthenticated]);

  // Show loading or wait while checking auth
  if (isCheckingAuth && requireAuth) {
    return null; // Or a loading spinner
  }

  if (requireAuth && !isAuthenticated) {
    // Redirect to login page, but save the attempted location
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  if (!requireAuth && isAuthenticated) {
    // If user is authenticated and trying to access auth pages, redirect to dashboard
    return <Navigate to="/events/" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
